import "chart.js/auto";
import React, {forwardRef, useEffect, useRef, useState, useImperativeHandle} from "react";
import Config from "../Config";
import token from "../AuthToken";
import {Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import _ from "../Trans";
import Select from "react-select";
import Currency from "./Currency";
import {Line} from "react-chartjs-2";
import BarLoader from "react-spinners/BarLoader";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faRightFromBracket} from "@fortawesome/free-solid-svg-icons";
import ReactGA from "react-ga4";

const defaultPlayer = {
    id: 0,
    name: '',
    image_link: Config.emptyPlayerImage,
    price: 0,
    goals_ratio: 0,
    position: 0,
    stops_ratio: 0,
    last_valuation: 0,
    valuation_average: 0,
    position_id: 0
}

const pricePerPoint = function(price, value) {
    return (price / value).toFixed(2)
}

const calculateTotals = function (obj) {
    try {
        obj['points'] = obj.valuations.reduce((acum, obj) => acum + obj.value, 0);
        obj['total_goals'] = obj.goals.reduce((acum, obj) => acum + obj.value, 0);
        obj['total_assists'] = obj.assists.reduce((acum, obj) => acum + obj.value, 0);
        obj['total_stops'] = obj.stops.reduce((acum, obj) => acum + obj.value, 0);
    }
    catch(e) {
        console.log("Catching errors calculating totals");
    }
}

const ComparePlayers = forwardRef((props, ref) => {
    const [show, setShow] = useState(false);
    // loading o barLoader? y por que empiezan diferente?
    const [barLoader, setBarLoader] = useState(false);
    const lineChart = useRef();
    const [player1, setPlayer1] = useState(null);
    const [player2, setPlayer2] = useState(null);
    const [playerList, setPlayerList] = useState([]);

    const data = {
      labels: [],
      datasets: [
        {
          label: "",
          data: [0],
          fill: false,
          backgroundColor: "rgba(75,192,192,0.2)",
          borderColor: "rgba(75,192,192,1)"
        },
        {
          label: "",
          data: [0],
          fill: false,
          borderColor: "#742774"
        }
      ]
    };
    const [chartData, setChartData] = useState(data);
    const chartOptions = {
        maintainAspectRatio: false,
        scales: {
         y: {
           beginAtZero: true,
         },
         x: {
             offset: true
         }
       }
    }

    useImperativeHandle(ref, () => ({
        toggle
    }));

    const [player1Data, setPlayer1Data] = useState(defaultPlayer);
    const [player2Data, setPlayer2Data] = useState(defaultPlayer);

    const toggle = () => {
        setShow(!show);
    }

    const hide = () => {
        setShow(false);
    }

    const closed = function() {
    }

    const getApiUrl = function() {
        return Config.playerListUrl + "&id="+player1+"|"+player2;
    }

    const prepareData = function(data1, data2) {
        try {
            calculateTotals(data1);
            calculateTotals(data2);
        }
        catch(e) {
            console.error("Calculating totals", e);
        }
        setPlayer1Data(data1);
        setPlayer2Data(data2);
    }

    const prepareDatasetForChart = function(valuations, dataset, prices, i) {
        try {
            let value;
            if(prices != null) {
                let sum = dataset.slice(0, i + 1).reduce((acum, obj) => acum + obj.value, 0);
                if(sum === 0) {
                    sum = 1;
                }
                value = Math.round(prices[i].value / sum);
                console.log("price, sum and value is ", prices[i].value, sum, value);
            }
            else {
                value = dataset[i].value;
            }
            valuations.push(value);
        }
        catch(e) {
            console.log("Error calculating dataset for chart data", e);
            valuations.unshift(null);
        }
    }

    const prepareChartData = async function({dataset1, dataset2, prices1, prices2, name1, name2}) {
        dataset1 = dataset1 != null ? dataset1 : player1Data?.valuations;
        dataset2 = dataset2 != null ? dataset2 : player2Data?.valuations;
        if(dataset1 == null || dataset2 == null) {
            return;
        }
        name1 = name1 != null ? name1 : player1Data?.name;
        name2 = name2 != null ? name2 : player2Data?.name;

        let dataCopy = data;

        let valuations1 = [];
        let valuations2 = [];
        let i;
        for(i=0; i<Math.max(dataset1.length, dataset2.length); i++) {
            prepareDatasetForChart(valuations1, dataset1, prices1, i);
            prepareDatasetForChart(valuations2, dataset2, prices2, i);
        }
        dataCopy.datasets[0].data = valuations1;
        dataCopy.datasets[0].label = name1;
        dataCopy.datasets[1].data = valuations2;
        dataCopy.datasets[1].label = name2;
        let labelsLength = Math.max(valuations1.length, valuations2.length);
        dataCopy.labels = [];
        for(i=0; i<labelsLength; i++) {
            dataCopy.labels[i] = '';
        }
        setChartData(dataCopy);
    }

    const fetchPlayers = async() => {
        setBarLoader(true);
        await fetch(
            getApiUrl(), {
            headers: {
                Authorization: 'Token '+token,
                'Content-Type': 'application/json'
            }
        })
        .then((res) => res.json())
        .then((d) => {
            if(player1 === player2) {
                prepareData(d.results[0], d.results[0]);
            }
            else if(d.results[0].id === player1) {
                prepareData(d.results[0], d.results[1]);
            }
            else {
                prepareData(d.results[1], d.results[0]);
            }
        })
        .catch((e) => console.log("Error fetching data", e))
        .finally(() => setBarLoader(false));

        try {
            const labelName1 = player1Data?.name ?? "-";
            const labelName2 = player2Data?.name ?? "-";
            ReactGA.event({
                category: "compare",
                action: "compare",
                label: labelName1+" "+labelName2
            })
        } catch(e) {
            console.error(e);
        }
    }

    const player1Selected = function(e) {
        setPlayer1(e?.value);
        if(e === null) {
            setPlayer1Data(defaultPlayer);
        }
    }

    const player2Selected = function(e) {
        setPlayer2(e?.value);
        if(e === null) {
            setPlayer2Data(defaultPlayer);
        }
    }

    useEffect(() => {
        if(props?.playerId !== undefined) {
            setPlayer1(parseInt(props.playerId));
        }
    }, [props]);  // eslint-disable-line

    useEffect(() => {
        fetchPlayers()
    }, [player1, player2]);  // eslint-disable-line

    useEffect(function() {
        setBarLoader(true);
        fetch(
            Config.playersListUrl, {
                headers: {
                    Authorization: 'Token '+token,
                    'Content-Type': 'application/json'
            }
        })
        .then((res) => res.json())
        .then((d) => {
            setPlayerList(d.results);
        })
        .then(() => setBarLoader(false))
        .catch(() => setBarLoader(false))
    }, []);  // eslint-disable-line

    useEffect(() => {
        prepareChartData({dataset1: player1Data?.valuations, dataset2: player2Data?.valuations});
    }, [player1Data, player2Data]);  // eslint-disable-line

    const drawAverageChart = function(e) {
        prepareChartData({dataset1: player1Data?.valuations, dataset2: player2Data?.valuations});
    }

    const drawPointsChart = function(e) {
        prepareChartData({dataset1: player1Data?.valuations, dataset2: player2Data?.valuations, prices1: player1Data?.prices, prices2: player2Data?.prices});
    }

    const drawGoalsChart = function(e) {
        prepareChartData({dataset1: player1Data?.goals, dataset2: player2Data?.goals, prices1: player1Data?.prices, prices2: player2Data?.prices});
    }

    const drawAssistsChart = function(e) {
        prepareChartData({dataset1: player1Data?.assists, dataset2: player2Data?.assists, prices1: player1Data?.prices, prices2: player2Data?.prices});
    }

    const drawStopsChart = function(e) {
        prepareChartData({dataset1: player1Data?.stops, dataset2: player2Data?.stops, prices1: player1Data?.prices, prices2: player2Data?.prices});
    }

    return (
        <div ref={ref}>
        <Modal isOpen={show} toggle={toggle} onClosed={closed} id="player-stats-modal" tabIndex="-1" role="dialog" fade={false} style={{color: 'black'}}>
            <ModalHeader>
                <div className="d-flex justify-content-between w-100">
                    <div id="exampleModalLabel">{_('Compare players')}</div>
                    <div className="float-right mt-1">
                        <button onClick={toggle} type="button"
                                className="close float-right text-right"
                                data-dismiss="modal" aria-label="Close">
                          <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                </div>
            </ModalHeader>
            <ModalBody className="mb-2 mt-3">
                <table className='w-100 player-comparator'>
                    <thead>
                        <tr>
                            <th>
                                <img style={{maxWidth: '100px', minHeight: '100px'}} src={player1Data?.image_link} className="image-squad" loading="lazy" alt={player1Data?.name} title={player1Data?.name}/>
                            </th>
                            <th></th>
                            <th>
                                <img style={{maxWidth: '100px', minHeight: '100px'}} src={player2Data?.image_link} loading="lazy" alt={player2Data?.name} title={player2Data?.name}/>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td colSpan={3}>
                                <div className="d-flex justify-content-between">
                                    <div style={{width: '45%'}}>
                              <Select onChange={player1Selected} options={playerList} isClearable={true} placeholder={_('Select')} styles={{
                                    container: (base) => ({
                                        ...base,
                                        maxWidth: '100%',
                                        minWidth: '100%'
                                    }),
                                }}/>
                                    </div>
                                    <div style={{width: '45%'}}>
                              <Select onChange={player2Selected} options={playerList} isClearable={true} placeholder={_('Select')} styles={{   container: (base) => ({
                                        ...base,
                                        maxWidth: '100%',
                                        minWidth: '100%'
                                    }),
                                }}/>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={3} style={{paddingTop: "0.5rem", paddingBottom: "0.5rem"}}>
                                <div className="w-100 d-flex justify-content-center">
                                    <BarLoader
                                        color="#23a6da"
                                        loading={barLoader}
                                        width={400}
                                        aria-label="Loading Spinner"
                                        data-testid="loader"
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td><Currency value={player1Data?.price}/></td>
                            <td className="player-comparator-center-td">{_('Current price')}</td>
                            <td className="player-comparator-right-td"><Currency value={player2Data?.price}/></td>
                        </tr>
                        { (player1Data?.position_id === Config.GOALKEEPER || player2Data?.position_id === Config.GOALKEEPER) &&
                            <tr>
                                <td>{player1Data?.stopped_shots_ratio}</td>
                                <td className="player-comparator-center-td">{_('Stops ratio')}</td>
                                <td align='right' className="player-comparator-right-td">{player2Data?.stopped_shots_ratio}</td>
                            </tr>
                        }
                        <tr>
                            <td>{player1Data?.goals_ratio}</td>
                            <td className="player-comparator-center-td">{_('Goals ratio')}</td>
                            <td align='right' className="player-comparator-right-td">{player2Data?.goals_ratio}</td>
                        </tr>
                        <tr>
                            <td>{player1Data?.last_valuation ?? "-"}</td>
                            <td onClick={drawAverageChart} className="link player-comparator-center-td player-comparator-link">{_('Last valuation')}</td>
                            <td align='right' className="player-comparator-right-td">{player2Data?.last_valuation ?? "-"}</td>
                        </tr>
                        <tr>
                            <td>{player1Data?.valuation_average}</td>
                            <td className="player-comparator-center-td">{_('Average points')}</td>
                            <td align='right' className="player-comparator-right-td">{player2Data?.valuation_average}</td>
                        </tr>
                        <tr>
                            <td>
                                <Currency value={pricePerPoint(player1Data?.price, player1Data?.points)}/>
                            </td>
                            <td onClick={drawPointsChart} className="link player-comparator-center-td player-comparator-link">{_('Price Per Point')}</td>
                            <td align='right' className="player-comparator-right-td">
                                <Currency value={pricePerPoint(player2Data?.price, player2Data?.points)}/>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <Currency value={pricePerPoint(player1Data?.price, player1Data?.total_goals)}/>
                            </td>
                            <td onClick={drawGoalsChart} className="link player-comparator-center-td player-comparator-link">{_('Price Per Goal')}</td>
                            <td align='right' className="player-comparator-right-td">
                                <Currency value={pricePerPoint(player2Data?.price, player2Data?.total_goals)}/>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <Currency value={pricePerPoint(player1Data?.price, player1Data?.total_assists)}/>
                            </td>
                            <td onClick={drawAssistsChart} className="link player-comparator-center-td player-comparator-link">{_('Price Per Assist')}</td>
                            <td align='right' className="player-comparator-right-td">
                                <Currency value={pricePerPoint(player2Data?.price, player2Data?.total_assists)}/>
                            </td>
                        </tr>
                        { (player1Data?.position_id === Config.GOALKEEPER || player2Data?.position_id === Config.GOALKEEPER) &&
                        <tr>
                            <td>
                                <Currency value={pricePerPoint(player1Data?.price, player1Data?.total_stops)}/>
                            </td>
                            <td onClick={drawStopsChart} className="link player-comparator-center-td player-comparator-link">{_('Price Per Stop')}</td>
                            <td align='right' className="player-comparator-right-td">
                                <Currency value={pricePerPoint(player2Data?.price, player2Data?.total_stops)}/>
                            </td>
                        </tr>
                        }
                        <tr>
                            <td colSpan={3} style={{paddingTop: "20px"}}>
                                <Line data={chartData} width={100} height={200} options={chartOptions} ref={lineChart} redraw={true}/>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <br/>
               {player1Data?.infoLink?.length > 0 && player1Data.infoLink.includes("asobal") &&
                    <a href={player1Data?.infoLink} target='_blank' rel="noreferrer">
                       <FontAwesomeIcon icon={faRightFromBracket}/> &nbsp; {_('Stats at ASOBAL')}
                    </a>
               }
                <div className="d-none" id="stats-processor"></div>
            </ModalBody>
            <ModalFooter>
                  <button type="button" className="btn btn-secondary" data-dismiss="modal" onClick={hide}>
                      {_('Close')}
                  </button>
            </ModalFooter>
        </Modal>
        </div>

    )
});

export default ComparePlayers;
